import { useMemo } from "react";
import { useInboxLocalState } from "./inboxLocalState";

export type InboxReadPresentation = { scope: string; key: string } | null;

/** Read only on entering a visible detail, preserving an explicit "Mark as unread". */
export function newlyOpenedInboxItem(
  previous: InboxReadPresentation,
  current: InboxReadPresentation,
): string | null {
  return current && (previous?.scope !== current.scope || previous.key !== current.key)
    ? current.key
    : null;
}

/**
 * Prefix of the read-receipt key family. The keys themselves are dynamic
 * (one per server/subject pair), so the sign-out purge matches on this.
 */
export const INBOX_READ_STORAGE_KEY_PREFIX = "amoreg:inbox-read:v1:";

/** The inbox is personal: scope receipts to both the server and signed-in identity. */
export function inboxReadStorageKey(server: string, subject: string): string {
  return `${INBOX_READ_STORAGE_KEY_PREFIX}${JSON.stringify([server, subject])}`;
}

export function parseInboxReadState(raw: string): ReadonlySet<string> {
  try {
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === "string") : [],
    );
  } catch {
    return new Set();
  }
}

export function updateInboxReadState(raw: string, key: string, read: boolean): string {
  const keys = new Set(parseInboxReadState(raw));
  if (read) keys.add(key);
  else keys.delete(key);
  return JSON.stringify([...keys]);
}

export function useInboxReadState(scope: string | null) {
  const { raw, update, available } = useInboxLocalState(scope);
  const readKeys = useMemo(() => parseInboxReadState(raw), [raw]);
  return {
    readKeys,
    canSetRead: available,
    setRead: (key: string, read: boolean) => update((raw) => updateInboxReadState(raw, key, read)),
  };
}
